import {
  Box,
  FormHelperText,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useFetchIndustrySuggestions } from 'api/industries/industries.mutations.ts';
import { useKeyValues } from 'api/keyValues';
import { type IAdvertisement, type IPrefillParams, useCreateSubmission } from 'api/submission';
import {
  BUSINESS_DESCRIPTION_CHARACTER_LIMIT,
  dateQueryParamFormat,
  defaultBackendDateformat,
  defaultDebounceDelay,
  EFFECTIVE_DATE_ERROR_MESSAGE,
  FULLSTORY_EVENTS,
  genericErrorMessage,
  GOOGLE_TAG_EVENTS,
  STATE_LIST,
  VALIDATION_MESSAGES,
} from 'common/constants';
import { DatePicker } from 'components/lib/DatePicker';
import { FormControl } from 'components/lib/FormControl';
import { PrimaryButton } from 'components/lib/PrimaryButton';
import { PrimaryIndustrySearch } from 'components/lib/PrimaryIndustrySearch';
import type {
  PrimaryIndustry,
  PrimaryIndustrySearchProps,
} from 'components/lib/PrimaryIndustrySearch/PrimaryIndustrySearch.types';
import { Select } from 'components/lib/Select';
import { TertiaryButton } from 'components/lib/TertiaryButton';
import WarningBanner from 'components/lib/WarningBanner/WarningBanner';
import { PrimaryIndustrySearchMobile } from 'components/PrimaryIndustrySearchMobile';
import { displayToastMessage } from 'components/ToastMessage';
import { useFormik } from 'formik';
import {
  formatDate,
  getWCExclusionInfo,
  pushToGoogleDataLayer,
  trackEventFullStory,
} from 'helpers/utils.ts';
import { useQueryParams } from 'hooks/useSearchQueryParams';
import { debounce, isEmpty } from 'lodash-es';
import { DateTime } from 'luxon';
import { type FC, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFullscreenLoader } from 'store/features/UI';
import * as yup from 'yup';
import type {
  EligibilityFormValues,
  EligibilityQuestionsProps,
} from './EligibilityQuestions.types';

const today = DateTime.now().startOf('day');
const tomorrow = today.plus({ days: 1 }).toJSDate();
const maxDate = today.plus({ days: 90 }).toJSDate();

const businessDescriptionMinLength = 8 as const;

const eligibilityValidationSchema = yup.object({
  pol_tax_keyword: yup.string().required(),
  pol_state: yup.string().required(),
  pol_start_date: yup
    .date()
    .nullable()
    .default(null)
    .typeError(VALIDATION_MESSAGES.invalidDateFormat)
    .required()
    .min(tomorrow, `The date must be later than ${formatDate(today.toJSDate())}`)
    .max(maxDate, `The date cannot be later than ${formatDate(maxDate)}`),
  business_description: yup
    .string()
    .required('This field cannot be blank.')
    .min(businessDescriptionMinLength, 'Please type at least 8 characters.'),
});

// eslint-disable-next-line complexity
const EligibilityQuestions: FC<EligibilityQuestionsProps> = () => {
  const [state, setState] = useState<EligibilityFormValues>({} as EligibilityFormValues);
  const {
    utm_source,
    utm_medium,
    utm_campaign,
    utm_content,
    utm_term,
    utm_adgroup,
    gclid,
    utm_clickid,
    start_date,
    state: pol_state,
  } = useQueryParams<IAdvertisement & IPrefillParams>();

  const parsedStartDate = start_date
    ? DateTime.fromFormat(start_date, dateQueryParamFormat)
    : undefined;

  const pol_start_date =
    parsedStartDate && parsedStartDate.isValid ? parsedStartDate.toJSDate() : undefined;

  const { data, isLoading } = useKeyValues<PrimaryIndustry[]>({ key: 'primary-industry' });

  const { setFullscreenLoading } = useFullscreenLoader();
  const navigate = useNavigate();
  const {
    data: industrySuggestions,
    isPending: isFetchingIndustrySuggestions,
    mutateAsync: fetchIndustrySuggestions,
    reset: resetIndustrySuggestions,
  } = useFetchIndustrySuggestions();
  const { mutateAsync: createSubmission } = useCreateSubmission();

  const themeHook = useTheme();

  const isMobileLayout = useMediaQuery(themeHook.breakpoints.down('sm'));

  const formik = useFormik<EligibilityFormValues>({
    initialValues: {
      pol_tax_keyword: '',
      pol_start_date: pol_start_date || tomorrow,
      pol_state: pol_state ? pol_state.toUpperCase() : '',
      business_description: '',
    },
    validationSchema: eligibilityValidationSchema,
    onSubmit: async (values: EligibilityFormValues) => {
      if (formik.isValid) {
        try {
          setFullscreenLoading(true, 'Checking eligibility');

          const submission = await createSubmission({
            effective_date: state.pol_start_date
              ? formatDate(state.pol_start_date, defaultBackendDateformat)
              : '',
            us_state: values.pol_state!,
            primary_industry: state.pol_tax_keyword,
            advertisement: {
              utm_source,
              utm_medium,
              utm_campaign,
              utm_content,
              utm_term,
              utm_adgroup,
              gclid,
              utm_clickid,
            },
            business_description: values.business_description?.trim(),
            suggested_options: industrySuggestions,
            selected_option: values.pol_tax_keyword,
          });

          if (submission?.not_eligible_reason?.field_errors?.effective_date) {
            displayToastMessage('ERROR', EFFECTIVE_DATE_ERROR_MESSAGE);
            return;
          }

          pushToGoogleDataLayer({ event: GOOGLE_TAG_EVENTS.LEAD_ELIGIBLE });
          trackEventFullStory(GOOGLE_TAG_EVENTS.LEAD_ELIGIBLE);

          if (
            industrySuggestions?.length &&
            !industrySuggestions.includes(values.pol_tax_keyword)
          ) {
            trackEventFullStory(FULLSTORY_EVENTS.IGNORED_KEYWORD_SUGGESTIONS);
          }

          navigate(`/submission/${submission.id}`, { replace: true });
        } catch (_error) {
          displayToastMessage('ERROR', genericErrorMessage);
        } finally {
          setFullscreenLoading(false);
        }
      }
    },
  });

  // stable reference to our debounced helper
  const handleBusinessDescriptionChange = useMemo(
    () =>
      debounce(async (value: string) => {
        const trimmedInput = value.trim();

        if (trimmedInput?.length >= businessDescriptionMinLength) {
          fetchIndustrySuggestions({ prompt: trimmedInput });
        } else {
          resetIndustrySuggestions();
        }
      }, defaultDebounceDelay),
    [],
  );

  const primaryIndustrySelectProps: PrimaryIndustrySearchProps = {
    fieldName: 'pol_tax_keyword',
    placeholder: 'Start typing and select',
    formik,
    state,
    setState,
    industries: data?.value || [],
    loading: isLoading,
    suggestions: industrySuggestions,
  };

  useEffect(() => {
    setState?.((prevState) => ({
      ...prevState,
      ...formik.values,
    }));
  }, [formik.values]);

  const WCExclusionInfo = useMemo(
    () =>
      getWCExclusionInfo({
        policyTaxKeyword: formik.values?.pol_tax_keyword as string,
        policyState: formik.values.pol_state || '',
      }),
    [formik.values?.pol_tax_keyword, formik.values.pol_state],
  );

  return (
    <>
      <Box sx={{ mb: 6 }}>
        <Typography variant="h1" sx={{ mb: 3 }}>
          Let&apos;s see if THREE is available for your business.
        </Typography>

        <form
          id="checkEligibilityForm"
          name="checkEligibilityForm"
          noValidate
          onSubmit={formik.handleSubmit}
        >
          <Stack
            display="flex"
            flex-direction="column"
            gap={4.5}
            sx={{
              [`& .MuiFormControl-root`]: {
                gap: 1.5,
              },
            }}
          >
            <FormControl required>
              <Typography variant="h2">Policy start date</Typography>

              <DatePicker
                value={formik.values.pol_start_date as Date}
                onChange={(newValue) => {
                  formik.setFieldValue('pol_start_date', newValue);
                }}
                minDate={tomorrow}
                maxDate={maxDate}
                error={formik.touched.pol_start_date && Boolean(formik.errors.pol_start_date)}
                label="Policy start date"
                slotProps={{
                  textField: {
                    error: formik.touched.pol_start_date && Boolean(formik.errors.pol_start_date),
                    helperText: formik.touched?.pol_start_date && formik.errors?.pol_start_date,
                    inputProps: {
                      'data-test': 'policy-start-date-input',
                    },
                  },
                }}
                useDesktopDatePicker={false}
              />
            </FormControl>

            <Stack gap={1.2}>
              <Typography variant="h2">State</Typography>

              <FormControl
                error={formik.touched.pol_state && Boolean(formik.errors.pol_state)}
                renderLabel
                field={{
                  name: 'State',
                  code: 'pol_state',
                }}
              >
                <Select
                  value={formik.values.pol_state}
                  label="State"
                  testPrefix="State"
                  error={formik.touched.pol_state && Boolean(formik.errors.pol_state)}
                  onChange={(e) => formik.setFieldValue('pol_state', e.target.value)}
                >
                  {STATE_LIST.map(({ code, name }) => (
                    <MenuItem key={code} value={code} data-test={code}>
                      {name}
                    </MenuItem>
                  ))}
                </Select>

                {formik.touched?.pol_state && Boolean(formik.errors.pol_state) && (
                  <FormHelperText
                    sx={{
                      mx: 1.75,
                    }}
                  >
                    {formik.errors.pol_state as string}
                  </FormHelperText>
                )}
              </FormControl>
            </Stack>

            <FormControl sx={{ position: 'relative' }} required>
              <Typography variant="h2">Business description</Typography>

              <TextField
                minRows={3}
                placeholder="Describe the business in several words so we can suggest the best industry keyword"
                value={formik.values.business_description}
                error={
                  formik.touched.business_description &&
                  Boolean(formik.errors?.business_description)
                }
                helperText={
                  formik.touched?.business_description &&
                  Boolean(formik.errors?.business_description)
                    ? formik.errors?.business_description
                    : `${
                        formik?.values?.business_description?.trim().length || 0
                      }/${BUSINESS_DESCRIPTION_CHARACTER_LIMIT}`
                }
                onChange={(event) => {
                  formik.setFieldValue('business_description', event.target.value);
                  handleBusinessDescriptionChange(event.target.value);
                }}
                multiline
                required
                slotProps={{
                  htmlInput: {
                    'data-test': 'business_description',
                    maxLength: BUSINESS_DESCRIPTION_CHARACTER_LIMIT,
                  },
                }}
              />
              {isFetchingIndustrySuggestions && (
                <LinearProgress
                  color="secondary"
                  sx={{
                    position: 'absolute',
                    width: '100%',
                    left: 0,
                    bottom: -3,
                    borderBottomLeftRadius: 3,
                    borderBottomRightRadius: 3,
                  }}
                />
              )}
            </FormControl>

            <FormControl required>
              <Typography variant="h2">Primary industry</Typography>

              {isMobileLayout ? (
                <PrimaryIndustrySearchMobile
                  {...primaryIndustrySelectProps}
                  placeholder="Primary industry"
                />
              ) : (
                <PrimaryIndustrySearch {...primaryIndustrySelectProps} />
              )}
            </FormControl>
          </Stack>
        </form>
        {!isEmpty(WCExclusionInfo) && (
          <Box sx={{ mt: 2 }}>
            <WarningBanner isVisibleIcon>
              <Typography>{WCExclusionInfo?.warningText}</Typography>
            </WarningBanner>
          </Box>
        )}
      </Box>
      <PrimaryButton
        text="Check eligibility"
        fullWidth
        type="submit"
        data-test="check-eligibility"
        form="checkEligibilityForm"
      />
      <TertiaryButton
        text="Continue previous quote"
        onClick={() => navigate('/retrieve-quote')}
        data-test="continue_previous_quote"
      />
    </>
  );
};

export default EligibilityQuestions;
